import React from 'react'
import * as R from 'ramda'

import { fetchCandidates } from 'api/candidates'
import { useFetch, useLanguage, useModal } from 'hooks'
import { TAB_NAMES, getTabs, getFactors } from './model'
import { getCautions } from './tabs/cautions'
import { getVisibleCategories, getReportCategoryKeys } from './tabs/helpers'

const useTestsReportModalQueries = () => {
  const handleFetchCandidates = React.useCallback(
    async ({ id }) => await fetchCandidates({ id }),
    [],
  )

  return {
    handleFetchCandidates,
  }
}

const useTestsReportModalTabsState = state => {
  const { isOpen, project, response, language, candidates } = state

  const categoryKeys = getReportCategoryKeys(project?.profile)

  const visibleCategories =
    categoryKeys.length && response && response?.every(cat => cat?.scores)
      ? getVisibleCategories(
          categoryKeys,
          response?.map(response => response.scores),
          language,
        )
      : [[]]

  const noGMA = response?.every(response => isNaN(response?.scores?.GMA))

  const noCompetencies =
    R.isEmpty(categoryKeys) ||
    visibleCategories?.some(
      category => category?.length > 10 || category?.length < 1,
    ) ||
    visibleCategories?.some(category =>
      category.some(category => isNaN(category.score)),
    )

  const noPersonality = getFactors()
    .filter(factor => factor.parentId === TAB_NAMES.PERSONALITY)
    .every(factor =>
      response?.every(response => isNaN(response?.scores?.[factor.factor])),
    )

  const defaultTab =
    candidates.length === 1
      ? TAB_NAMES.INTRODUCTION
      : noCompetencies
      ? noGMA
        ? TAB_NAMES.PERSONALITY
        : TAB_NAMES.GMA
      : TAB_NAMES.COMPETENCY

  const [activeTab, setActiveTab] = React.useState(defaultTab)

  const [personalityTab, setPersonalityTab] = React.useState(TAB_NAMES.OVERVIEW)

  const tabs = getTabs(
    noCompetencies,
    noGMA,
    noPersonality,
    candidates,
    getCautions(response, candidates),
  )

  const handleTab = React.useCallback(
    id => {
      const isPersonalityFactor = Boolean(
        R.find(
          factor =>
            factor.id === id && factor.parentId === TAB_NAMES.PERSONALITY,
          getFactors(),
        ),
      )

      if (isPersonalityFactor) {
        setActiveTab(TAB_NAMES.PERSONALITY)
        setPersonalityTab(id)
      } else {
        setActiveTab(id)
        setPersonalityTab(TAB_NAMES.OVERVIEW)
      }
    },
    [setActiveTab, setPersonalityTab],
  )

  React.useEffect(() => {
    if (isOpen) {
      handleTab(defaultTab)
    }
  }, [handleTab, isOpen, defaultTab])

  return {
    state: { activeTab, tabs, personalityTab },
    handlers: { handleTab, setPersonalityTab, setActiveTab },
  }
}

const useTestsReportModalData = (state, handlers) => {
  const { candidates, isOpen } = state
  const { handleFetchCandidates } = handlers

  const language = useLanguage()

  const ids = candidates.map(candidate => candidate.id)

  const handleFetch = React.useCallback(async () => {
    if (R.or(!ids.length, !isOpen)) return null

    const response = await Promise.all(
      ids.map(async id => handleFetchCandidates({ id })),
    )

    return { response }
  }, [candidates, handleFetchCandidates, isOpen])

  const {
    state: { response, isFetching },
  } = useFetch(handleFetch)

  return {
    state: {
      language,
      response: response?.map(response => response?.response),
      isFetching,
    },
  }
}

export const useTestsReportModalState = state => {
  const { candidates, isOpen, project } = state

  const [selectedCompetency, setSelectedCompetency] = React.useState(0)
  const [selectedCandidates, setSelectedCandidates] = React.useState(
    candidates.reduce(
      (acc, candidate) => ({ ...acc, [candidate.id]: true }),
      {},
    ),
  )

  const { handleFetchCandidates } = useTestsReportModalQueries()

  const {
    state: { language, response, isFetching },
  } = useTestsReportModalData({ candidates, isOpen }, { handleFetchCandidates })

  const {
    state: { activeTab, tabs, personalityTab },
    handlers: { handleTab, setPersonalityTab, setActiveTab },
  } = useTestsReportModalTabsState({
    language,
    response,
    isOpen,
    project,
    candidates,
  })

  return {
    state: {
      activeTab,
      language,
      response,
      tabs,
      isFetching,
      personalityTab,
      selectedCompetency,
      selectedCandidates,
    },
    handlers: {
      handleTab,
      setPersonalityTab,
      setSelectedCandidates,
      setSelectedCompetency: competency => {
        setActiveTab(TAB_NAMES.COMPETENCY)
        setSelectedCompetency(competency)
      },
    },
  }
}

export const useTestsReportModal = () => {
  const { handleClose, handleOpen, isOpen } = useModal()

  return {
    handleTestsReportModalClose: handleClose,
    handleTestsReportModalOpen: handleOpen,
    isTestsReportModalOpen: isOpen,
  }
}
